import { SdkApi, SdkType } from '../interface';
import type JsSdkBase from '../core/js-sdk-base';
import type H5JsApi from '../core/h5-jsapi';
import { captureSdkResult, sdkLogger } from '../utils';

/**
 * 日志装饰器，用于统一记录sdk的调用日志
 * @param type sdk类型，默认为 this.sdkType
 * 注意，如果 jsSdkAuth 和 sdkLog 装饰器同时使用，
 * 需要将 jsSdkAuth 装饰器放在 sdkLog 装饰器之前，以确保调用顺序符合预期
 */
export function sdkLog(type?: SdkType | 'h5-jsapi') {
    /**
     * @param _ 类的实例
     * @param propKey 类的属性名
     * @param descriptor 类的属性描述符
     */
    return function (_: JsSdkBase | H5JsApi, propKey: SdkApi, descriptor: PropertyDescriptor) {
        const originalJsSdkApi = descriptor.value;
        type OriginalApiType = typeof originalJsSdkApi;
        descriptor.value = function (
            ...args: Parameters<OriginalApiType>
        ): ReturnType<OriginalApiType> {
            /**
             * 因为在 jsSdkAuth 的 fallback 逻辑中，是直接通过 JsSdkBase.prototype 获取的 fallbackApi
             * 所以当走到鉴权 fallback 逻辑时 jsSdkBase 的 sdkLog 装饰器中的 this 不会指向 jsSdkBase 实例
             * 而是会指向 XxxSdk 实例，导致 this.sdkType 的取值和预期不符。
             * 所以在 jsSdkBase 中使用 sdkLog 装饰器时需要通过 type 参数来指定当前 sdk 类型，其他场景不需要
             */
            const sdkType = type || this.sdkType;
            sdkLogger.info(`${sdkType} ${propKey} invoked`, { args });
            const result = originalJsSdkApi.apply(this, args);
            return captureSdkResult({
                result,
                sdkType,
                sdkApi: propKey
            });
        };

        return descriptor;
    };
}
